/**
 * WalkaroundButton — records video from the webcam/phone camera, uploads
 * to /walkaround. Optionally emails the customer a shareable link.
 */
import { useRef, useState } from "react";
import { Video, Square, Loader2, Send, Check } from "lucide-react";
import { api } from "../lib/api";

export default function WalkaroundButton({ leadId, onSaved }) {
  const [recording, setRecording] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sentUrl, setSentUrl] = useState(null);
  const videoRef = useRef(null);
  const mediaRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const startedAt = useRef(0);

  const start = async () => {
    setErr(""); setPreviewBlob(null); setPreviewUrl(null); setSentUrl(null);
    if (!navigator.mediaDevices?.getUserMedia) { setErr("Camera not available"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      const rec = new MediaRecorder(stream, { mimeType: "video/webm" });
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current);
        const blob = new Blob(chunks.current, { type: "video/webm" });
        setPreviewBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
      };
      mediaRef.current = rec;
      rec.start();
      startedAt.current = Date.now();
      setRecording(true);
      timer.current = setInterval(() => setElapsed(Math.round((Date.now() - startedAt.current) / 1000)), 250);
    } catch {
      setErr("Camera permission denied");
    }
  };
  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
    setElapsed(0);
  };
  const send = async (sendToCustomer) => {
    if (!previewBlob) return;
    setBusy(true); setErr("");
    try {
      const form = new FormData();
      form.append("video", previewBlob, "walkaround.webm");
      form.append("send_to_customer", String(sendToCustomer));
      const { data } = await api.post(`/dealer/lead/${leadId}/walkaround`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSentUrl(data.public_path);
      onSaved?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5 w-full">
      <div className="inline-flex items-center gap-2 flex-wrap">
        {!recording && !previewUrl && (
          <button onClick={start} disabled={busy}
                  className="text-[11px] font-extrabold text-white bg-fuchsia-600 hover:bg-fuchsia-700 px-2.5 py-1.5 rounded-full inline-flex items-center gap-1"
                  data-testid="walkaround-start">
            <Video size={11} /> Record walkaround
          </button>
        )}
        {recording && (
          <button onClick={stop}
                  className="text-[11px] font-extrabold text-white bg-red-600 hover:bg-red-700 px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 animate-pulse"
                  data-testid="walkaround-stop">
            <Square size={11} fill="currentColor" /> Stop ({elapsed}s)
          </button>
        )}
        {previewUrl && !sentUrl && (
          <>
            <button onClick={() => send(true)} disabled={busy}
                    className="text-[11px] font-extrabold text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1.5 rounded-full inline-flex items-center gap-1"
                    data-testid="walkaround-send">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send to customer
            </button>
            <button onClick={() => send(false)} disabled={busy}
                    className="text-[11px] font-bold text-[var(--ssc-navy)] bg-white border border-[#D9E1EA] hover:bg-[var(--ssc-paper)] px-2.5 py-1.5 rounded-full"
                    data-testid="walkaround-save">Save only</button>
          </>
        )}
        {sentUrl && (
          <span className="text-[11px] text-emerald-700 font-extrabold inline-flex items-center gap-1" data-testid="walkaround-done">
            <Check size={11} /> Saved
          </span>
        )}
      </div>
      {(recording || previewUrl) && (
        <video ref={videoRef} src={previewUrl || undefined} controls={!!previewUrl}
               className="w-full max-w-xs rounded-lg bg-black" muted={recording} playsInline />
      )}
      {err && <div className="text-[10px] text-red-600 font-bold">{err}</div>}
    </div>
  );
}
